import { Deserializer } from "jsonapi-serializer";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import PresetCustomField, { PresetCustomFieldWrapper } from "../models/PresetCustomField";
import { APIConnector } from "./api/APIConnector";
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { getDeviceSettingStatus } from "./utilities/StatusBuilder";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";

const apiConnector = new APIConnector();

const compileFilters = (profileId, listParameters) => {
    const profileFilter = {
        key: 'profile.id',
        label: 'Profile', // not displayed
        options: [{ label: profileId, value: profileId }],
        selected: [{ label: profileId, value: profileId }]
    };
    const actionSettingTypeFilter = {
        key: 'settingType.stSysname',
        label: 'Setting Type', // not displayed
        options: [{ label: 'P_*', value: 'P_*' }],
        selected: [{ label: 'P_*', value: 'P_*' }]
    };
    return getFilterClause(
        [...listParameters?.filters ?? [], profileFilter, actionSettingTypeFilter],
        listParameters?.searchTerm
    );
}

export const getPresetParameters = async (profileId: string, listParameters?: ListQuery): Promise<PresetCustomFieldWrapper> => {
    try {
        const filter: string = compileFilters(profileId, listParameters);
        let filterOutHiddenParameters = `${filter ? filter.replace('filter=', `filter=settingType.stHidden==0;`) : `&filter=settingType.stHidden==0`}`;
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filterOutHiddenParameters);
        const url = `setting?include=profile,settingType${paginationString}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                const presetParameter: PresetCustomField[] = data.map((presetParameter) => {
                    return {
                        id: presetParameter['id'],
                        name: presetParameter['trn-name'],
                        status: getDeviceSettingStatus(presetParameter['hierarchy-type'] ? presetParameter['hierarchy'] : 0)
                    }
                });

                return resolve({ rows: presetParameter, pagination });
            })
    });
} catch (error) {
    return new Promise((resolve, reject) => {
            reject(processServerError(error))
        })
}
};

export const getPresetParameterNamesWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const url = `setting?include=profile,settingType${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, response: any[]) => {
                const presetParameters: Name[] = response.map(setting => {
                    return {
                        value: setting['id'],
                        label: setting['trn-name'],
                        sdist: setting['s-dist'],
                        profileId: setting['profile']['id'],
                        settingIdToCopy: setting['id'],
                        columnId: setting['setting-type']['id'],
                    };
                })

                return resolve({ rows: presetParameters, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error))
        })
    }
}
